import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { ChevronRight, ChevronLeft, Loader2, MapPin, Building } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface WizardCoverageProps {
  onNext: () => void;
  onPrevious: () => void;
  siteId?: string;
}

interface Town {
  id: string;
  name: string;
  slug: string;
  is_published: boolean;
}

interface Area {
  id: string;
  name: string;
  slug: string;
  town_id: string | null;
  is_published: boolean;
}

export function WizardCoverage({ onNext, onPrevious, siteId }: WizardCoverageProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [towns, setTowns] = useState<Town[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [selectedTowns, setSelectedTowns] = useState<Set<string>>(new Set());
  const [selectedAreas, setSelectedAreas] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (siteId) {
      fetchCoverage();
    }
  }, [siteId]);

  const fetchCoverage = async () => {
    if (!siteId) return;

    try {
      const [townsRes, areasRes] = await Promise.all([
        supabase.from('towns').select('id, name, slug, is_published').eq('site_id', siteId).order('name'),
        supabase.from('areas').select('id, name, slug, town_id, is_published').eq('site_id', siteId).order('name'),
      ]);

      const townList = townsRes.data || [];
      const areaList = areasRes.data || [];

      setTowns(townList);
      setAreas(areaList);

      // Initialize selected sets based on is_published
      setSelectedTowns(new Set(townList.filter(t => t.is_published).map(t => t.id)));
      setSelectedAreas(new Set(areaList.filter(a => a.is_published).map(a => a.id)));
    } catch (error) {
      console.error('Error fetching coverage:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTown = (townId: string) => {
    setSelectedTowns(prev => {
      const next = new Set(prev);
      if (next.has(townId)) {
        next.delete(townId);
        // Also deselect all areas in this town
        areas.filter(a => a.town_id === townId).forEach(a => {
          setSelectedAreas(prevAreas => {
            const nextAreas = new Set(prevAreas);
            nextAreas.delete(a.id);
            return nextAreas;
          });
        });
      } else {
        next.add(townId);
      }
      return next;
    });
  };

  const toggleArea = (areaId: string) => {
    setSelectedAreas(prev => {
      const next = new Set(prev);
      if (next.has(areaId)) {
        next.delete(areaId);
      } else {
        next.add(areaId);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedTowns(new Set(towns.map(t => t.id)));
    setSelectedAreas(new Set(areas.map(a => a.id)));
  };

  const deselectAll = () => {
    setSelectedTowns(new Set());
    setSelectedAreas(new Set());
  };

  const handleSave = async () => {
    if (!siteId) return;

    setSaving(true);
    try {
      // Update towns
      for (const town of towns) {
        await supabase
          .from('towns')
          .update({ is_published: selectedTowns.has(town.id) })
          .eq('id', town.id);
      }

      // Update areas
      for (const area of areas) {
        await supabase
          .from('areas')
          .update({ is_published: selectedAreas.has(area.id) })
          .eq('id', area.id);
      }

      toast({ title: 'Coverage updated successfully' });
      onNext();
    } catch (error: any) {
      toast({
        title: 'Error saving coverage',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Group areas by town
  const areasByTown = areas.reduce((acc, area) => {
    const townId = area.town_id || 'unassigned';
    if (!acc[townId]) acc[townId] = [];
    acc[townId].push(area);
    return acc;
  }, {} as Record<string, Area[]>);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Select the towns and neighborhoods to include on your site. Deselected items will be hidden from navigation and search.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={selectAll}>
            Select All
          </Button>
          <Button variant="outline" size="sm" onClick={deselectAll}>
            Deselect All
          </Button>
        </div>
      </div>

      {towns.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No towns have been added to your site yet. Towns are typically set up during initial site creation.
        </div>
      ) : (
        <div className="space-y-4">
          {towns.map((town) => (
            <Card key={town.id} className={!selectedTowns.has(town.id) ? 'opacity-60' : ''}>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id={`town-${town.id}`}
                    checked={selectedTowns.has(town.id)}
                    onCheckedChange={() => toggleTown(town.id)}
                  />
                  <Label htmlFor={`town-${town.id}`} className="flex items-center gap-2 cursor-pointer">
                    <MapPin className="h-4 w-4 text-primary" />
                    <CardTitle className="text-base">{town.name}</CardTitle>
                  </Label>
                </div>
              </CardHeader>

              {areasByTown[town.id] && areasByTown[town.id].length > 0 && selectedTowns.has(town.id) && (
                <CardContent className="pt-2">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 pl-6">
                    {areasByTown[town.id].map((area) => (
                      <div key={area.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`area-${area.id}`}
                          checked={selectedAreas.has(area.id)}
                          onCheckedChange={() => toggleArea(area.id)}
                        />
                        <Label 
                          htmlFor={`area-${area.id}`} 
                          className="flex items-center gap-1 text-sm cursor-pointer"
                        >
                          <Building className="h-3 w-3 text-muted-foreground" />
                          {area.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Summary */}
      <div className="bg-muted rounded-lg p-4">
        <div className="flex items-center justify-between text-sm">
          <span>Selected: {selectedTowns.size} towns, {selectedAreas.size} neighborhoods</span>
          <span className="text-muted-foreground">
            Total: {towns.length} towns, {areas.length} neighborhoods
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button variant="outline" onClick={onPrevious}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>

        <Button onClick={handleSave} disabled={saving}>
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Save & Continue
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
